import type { DateTime } from "luxon";

export enum OtCause {
	ScheduledOverLimit = "scheduled_over_limit",
	PickedUp = "picked_up",
	AddedByManager = "added_by_manager",
	RanLong = "ran_long",
	UnplannedShift = "unplanned_shift",
	UnknownOrigin = "unknown_origin",
}

export enum OtBaselineKind {
	PreviousPeriod = "previous_period",
	PreviousMonth = "previous_month",
	TrailingAverage = "trailing_average",
}

export enum OtConfidenceState {
	Ok = "ok",
	Limited = "limited",
	Unavailable = "unavailable",
}

export enum OtConfidenceReason {
	NoStructuralHours = "no_structural_hours",
	UnknownOriginShare = "unknown_origin_share",
	BaselinePartial = "baseline_partial",
	EstimatedWages = "estimated_wages",
	WagesHidden = "wages_hidden",
}

export enum OtMovementDimension {
	Cause = "cause",
	Role = "role",
}

export enum OtBaselineCoverage {
	Full = "full",
	Partial = "partial",
	None = "none",
}

export enum OtDirection {
	Up = "up",
	Down = "down",
	Flat = "flat",
}

export enum OtMoverShare {
	AlmostAll = "almost_all",
	Most = "most",
	Largest = "largest",
}

export enum OtBreakdownDimension {
	Cause = "cause",
	Role = "role",
	/** Keys `"0"`–`"6"`, Sunday `0`. */
	Weekday = "weekday",
	/** Keys `"HH:00"` or `"unknown"`. */
	ShiftStart = "shift_start",
	/** Keys `"<weekday>|<HH:00>"`. */
	WeekdayShift = "weekday_shift",
	Location = "location",
	/** Keys are nurse ids; `label` is the name in the department's name order. */
	Person = "person",
}

export const OT_UNASSIGNED_ROLE = "unassigned";

/** What every overtime-overview request is measured over, so a drill-down always adds up to the overview. */
export type OtScope = {
	startDate: DateTime;
	endDate: DateTime;
	comparison: OtBaselineKind;
	isActual: boolean;
	estimateMissingWage: boolean;
};

export type OtTotals = {
	ot_hours: number;
	ot_hours_per_week: number | null;
	/** A fraction (0–1); null when the period has no paid hours. */
	ot_share_of_paid_hours: number | null;
	/** Null when the cost is withheld, never zero. */
	ot_cost: number | null;
};

export type OtMovement = {
	dimension: OtMovementDimension;
	key: string;
	label: string;
	current_hours: number;
	baseline_hours: number | null;
	delta_hours: number | null;
	unusual: boolean | null;
	rank: number;
};

export type OtConfidence = {
	origin: OtConfidenceState;
	cost: OtConfidenceState;
	trend: OtConfidenceState;
	reasons: OtConfidenceReason[];
};

export type OtMovementRef = {
	dimension: OtMovementDimension;
	key: string;
	label: string;
	delta_hours: number | null;
};

export type OtVerdictLead = OtMovementRef & { share: OtMoverShare };

export type OtVerdict = {
	has_overtime: boolean;
	direction: OtDirection | null;
	delta_hours: number | null;
	lead_role: OtVerdictLead | null;
	lead_cause: OtVerdictLead | null;
	net_hides_movement: boolean;
	unusual: OtMovementRef | null;
	baseline_coverage: OtBaselineCoverage;
};

export type OtBalance = {
	gross_increase: number;
	/** Zero or negative. */
	gross_decrease: number;
	net: number;
};

export type OtOverview = {
	start_date: DateTime;
	end_date: DateTime;
	ot_weeks: DateTime[];
	baseline_kind: OtBaselineKind;
	current: OtTotals;
	baseline: OtTotals | null;
	movements: OtMovement[];
	balance: OtBalance | null;
	confidence: OtConfidence;
	verdict: OtVerdict;
};

export type OtBreakdownRow = {
	key: string;
	label: string;
	current_hours: number;
	baseline_hours: number | null;
	delta_hours: number | null;
	/** A fraction (0–1); null when the slice has no current hours. */
	share_of_current: number | null;
};

export type OtBreakdownRest = {
	row_count: number;
	current_hours: number;
	baseline_hours: number | null;
};

export type OtBreakdown = {
	start_date: DateTime;
	end_date: DateTime;
	baseline_kind: OtBaselineKind;
	dimension: OtBreakdownDimension;
	cause: OtCause | null;
	role: string | null;
	current_hours: number;
	baseline_hours: number | null;
	rows: OtBreakdownRow[];
	rest: OtBreakdownRest | null;
	origin: OtConfidenceState;
	reasons: OtConfidenceReason[];
};

export type OtEvidenceCause = {
	cause: OtCause;
	hours: number;
};

export type OtEvidencePiece = {
	piece_id: string;
	scheduled_shift_id: string | null;
	worked_start: DateTime;
	worked_end: DateTime | null;
	scheduled_start: DateTime | null;
	scheduled_end: DateTime | null;
	ot_hours: number;
	causes: OtEvidenceCause[];
};

export type OtEvidenceRecord = {
	shift_id: string;
	day: DateTime;
	nurse_id: number;
	/** Already in the department's name order. */
	nurse_name: string;
	role: string;
	worked_start: DateTime;
	worked_end: DateTime | null;
	/** Null when the punch matched no plan or several. */
	scheduled_start: DateTime | null;
	scheduled_end: DateTime | null;
	location: string | null;
	ot_hours: number;
	causes: OtEvidenceCause[];
	pieces: OtEvidencePiece[];
};

export type OtEvidencePage = {
	records: OtEvidenceRecord[];
	total: number;
	offset: number;
	limit: number;
};

export enum OtNoteLineKind {
	Headline = "headline",
	Role = "role",
	Finding = "finding",
	Staffing = "staffing",
	NoFindings = "no_findings",
	Suppressed = "suppressed",
	DataQuality = "data_quality",
}

export enum OtNoteLanguage {
	En = "en",
	Es = "es",
	He = "he",
}

export type OtNoteLine = {
	kind: OtNoteLineKind;
	finding_id: string | null;
	/** One of the backend's `OtNoteTemplate` names; the text is already rendered. */
	template: string;
	text: string;
};

export type OtNote = {
	template_version: number;
	language: OtNoteLanguage;
	start_date: DateTime;
	end_date: DateTime;
	lines: OtNoteLine[];
	/** Legacy saved notes have no live reconciliation context. */
	headline_context?: {
		context_fingerprint: string;
		warnings: import("./otPagePlan").OtPlanSection["warnings"];
		unknown_origin_share: number | null;
	};
};

export enum OtSummarySource {
	Analysis = "analysis",
	Template = "template",
	Pending = "pending",
}

export enum OtAudience {
	Scheduler = "scheduler",
}

export const OT_SUMMARY_WINDOW_DAYS = 30;

export type OtShownCause = {
	rank: number;
	merged_because: string | null;
	why: string;
	people: string[] | null;
};

export type OtShownAction = {
	kind: string;
	why: string;
	cause_ranks: number[];
};

export type OtAnalysisCaveat = {
	flag: string;
	why: string;
};

export type OtShownAnalysis = {
	summary: string;
	paragraph: string;
	causes: OtShownCause[];
	actions: OtShownAction[];
	data_caveats: OtAnalysisCaveat[];
};

type OtSummaryWritten = {
	audience: OtAudience;
	start_date: DateTime;
	end_date: DateTime;
	/** When the saved text was written; the API may still send null. */
	as_of: DateTime | null;
	pack_hash: string;
	version: string;
};

export type OtSummary =
	| (OtSummaryWritten & { source: OtSummarySource.Analysis; analysis: OtShownAnalysis; note: null })
	| (OtSummaryWritten & { source: OtSummarySource.Template; analysis: null; note: OtNote })
	| {
			source: OtSummarySource.Pending;
			audience: OtAudience;
			start_date: null;
			end_date: null;
			as_of: null;
			pack_hash: null;
			version: null;
			analysis: null;
			note: null;
	  };
